'use client'

import { useToastStore } from '@trustoo/components/ui/toaster/hooks/useToastStore'
import type { ToastActions, ToastState } from '@trustoo/components/ui/toaster/types/toastTypes'
import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'

const ToastStateContext = createContext<ToastState | undefined>(undefined)
const ToastActionsContext = createContext<ToastActions | undefined>(undefined)

/**
 * The app-wide toast channel, rendered by `Toaster`. Actions have a separate context so producers
 * do not subscribe to toast state. Toasts inside a `BaseModal` have their own channel, see
 * `useBaseModalToast`.
 */
export interface ToastProviderProps {
    children: ReactNode
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
    const { toasts, actions } = useToastStore()

    return (
        <ToastActionsContext.Provider value={actions}>
            <ToastStateContext.Provider value={toasts}>{children}</ToastStateContext.Provider>
        </ToastActionsContext.Provider>
    )
}

export const useToastActions = (): ToastActions => {
    const context = useContext(ToastActionsContext)
    if (context === undefined) {
        throw new Error('useToastActions must be used within a ToastProvider')
    }
    return context
}

export const useToastState = (): ToastState => {
    const context = useContext(ToastStateContext)
    if (context === undefined) {
        throw new Error('useToastState must be used within a ToastProvider')
    }
    return context
}
